import React, { useRef, useState } from 'react';
import type { Attachment } from '../types';
import { uploadResusFile, deleteResusFile, getResusFileSasUrl } from '../api/resusAdminApi';
import DeleteConfirmationDialog from '../../../components/dialogs/DeleteConfirmationDialog';
import { validateFile, describeAccept, formatFileSize, extensionsFrom } from '../../../utils/file-validation';

// Kept in step with ALLOWED_MIME / ALLOWED_EXT in resusAdminRoutes.js.
const ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
};
const MAX_FILE_BYTES = 20 * 1024 * 1024; // resusAdminRoutes.js MAX_FILE_BYTES
const ACCEPT_ATTR = [...extensionsFrom(ACCEPT), ...Object.keys(ACCEPT)].join(',');

interface Props {
  hospital: string;
  attachments: Attachment[];
  onChange: (next: Attachment[]) => void;
  onError: (msg: string) => void;
}

// Returns true if the file path is a fresh Azure-style upload; legacy bare
// filenames (e.g. "cardiac_arrest_algorithm.pdf") never get deleted from Azure
// because they don't live there in the first place.
function isAzurePath(file: string): boolean {
  return /^apps\/resus\//.test(file);
}

const AttachmentList: React.FC<Props> = ({ hospital, attachments, onChange, onError }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null); // null = adding new; number = replacing
  const [busy, setBusy] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const onPickFile = (replaceIndex: number | null) => {
    setPendingIndex(replaceIndex);
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!hospital) {
      onError('בחר בית חולים לפני העלאת קובץ');
      setPendingIndex(null);
      return;
    }

    // Checked here rather than left to the server: a 30MB scan otherwise
    // uploads for however long the connection takes and comes back as a bare
    // "בעיה בהעלאת הקובץ".
    const rejection = validateFile(file, { accept: ACCEPT, maxSize: MAX_FILE_BYTES });
    if (rejection) {
      onError(`${rejection.message} סוגים נתמכים: ${describeAccept(ACCEPT)}.`);
      setPendingIndex(null);
      return;
    }

    const replacingIndex = pendingIndex;
    setBusy(true);
    try {
      const result = await uploadResusFile(hospital, file);

      if (replacingIndex === null) {
        // Append a new attachment with default Hebrew display name "אלגוריתם"
        onChange([
          ...attachments,
          { name: file.name.replace(/\.[^.]+$/, ''), file: result.relativePath }
        ]);
      } else {
        // Replace existing — best-effort delete of the old blob if it was an Azure file
        const old = attachments[replacingIndex];
        if (old && isAzurePath(old.file)) {
          deleteResusFile(hospital, old.file).catch((err) => {
            console.warn('Failed to delete replaced attachment:', err);
          });
        }
        const next = attachments.map((a, i) =>
          i === replacingIndex ? { ...a, file: result.relativePath } : a
        );
        onChange(next);
      }
    } catch (err: any) {
      console.error('Upload failed:', err);
      onError(err?.serverError?.hebMessage || 'העלאת הקובץ נכשלה');
    } finally {
      setBusy(false);
      setPendingIndex(null);
    }
  };

  const onRenameAttachment = (index: number, name: string) => {
    onChange(attachments.map((a, i) => i === index ? { ...a, name } : a));
  };

  const onDeleteAttachment = (index: number) => {
    const item = attachments[index];
    if (!item) return;
    setDeleteIndex(index);
  };

  const confirmDeleteAttachment = async () => {
    if (deleteIndex === null) return;
    const item = attachments[deleteIndex];
    if (!item) {
      setDeleteIndex(null);
      return;
    }

    if (isAzurePath(item.file) && hospital) {
      try {
        await deleteResusFile(hospital, item.file);
      } catch (err) {
        console.warn('Failed to delete attachment from storage:', err);
      }
    }
    onChange(attachments.filter((_, i) => i !== deleteIndex));
    setDeleteIndex(null);
  };

  const onPreview = async (index: number) => {
    const item = attachments[index];
    if (!item || !hospital) return;
    if (!isAzurePath(item.file)) {
      // Legacy: open the public asset path used by the consumer app today.
      window.open(`/assets/${hospital}/protocols/${item.file}`, '_blank');
      return;
    }
    try {
      const url = await getResusFileSasUrl(hospital, item.file);
      window.open(url, '_blank');
    } catch (err) {
      onError('לא ניתן לפתוח קובץ לתצוגה מקדימה');
    }
  };

  return (
    <div className="resus-admin__field">
      <label className="resus-admin__field-label">קבצים מצורפים</label>
      <p className="resus-admin__field-hint">
        ניתן לצרף עד {formatFileSize(MAX_FILE_BYTES)} לקובץ. סוגים נתמכים: {describeAccept(ACCEPT)}.
        קבצים חדשים נשמרים בנתיב <code>apps/resus/{hospital || '<hospital>'}/</code>.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {attachments.map((a, i) => (
          <div key={i} className="resus-admin__attachment-row">
            <input
              type="text"
              className="resus-admin__input"
              value={a.name}
              onChange={(e) => onRenameAttachment(i, e.target.value)}
              placeholder="שם תצוגה"
              style={{ flex: 1, maxWidth: 200 }}
            />
            <span className="resus-admin__attachment-row__filename" title={a.file}>
              {a.file.split('/').pop()}
            </span>
            <button
              type="button"
              className="resus-admin__btn resus-admin__btn--ghost"
              onClick={() => onPreview(i)}
              style={{ padding: '6px 10px', fontSize: '0.85rem' }}
            >
              תצוגה
            </button>
            <button
              type="button"
              className="resus-admin__btn resus-admin__btn--ghost"
              onClick={() => onPickFile(i)}
              disabled={busy}
              style={{ padding: '6px 10px', fontSize: '0.85rem' }}
            >
              {busy && pendingIndex === i ? 'מעלה...' : 'החלף קובץ'}
            </button>
            <button
              type="button"
              className="resus-admin__delete-btn"
              onClick={() => onDeleteAttachment(i)}
              disabled={busy}
            >
              מחק
            </button>
          </div>
        ))}

        <button
          type="button"
          className="resus-admin__btn resus-admin__btn--accent"
          onClick={() => onPickFile(null)}
          disabled={busy || !hospital}
          style={{ alignSelf: 'flex-start' }}
          title={!hospital ? 'בחר בית חולים כדי להעלות קבצים' : undefined}
        >
          {busy && pendingIndex === null ? 'מעלה...' : '+ העלאת קובץ'}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTR}
        style={{ display: 'none' }}
        onChange={onFileSelected}
      />

      <DeleteConfirmationDialog
        open={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        onConfirm={confirmDeleteAttachment}
        title={`למחוק את הקובץ "${deleteIndex !== null ? (attachments[deleteIndex]?.name || '') : ''}"?`}
        showError={false}
        errorMessage=""
        onCloseError={() => {}}
      />
    </div>
  );
};

export default AttachmentList;
